<template>
  <b-form @submit.prevent="submit">
    <BlockDevice
      :driver-id="driver.id"
      :device-id="toBeDeletedDeviceId"
      @done="$emit('device-blocked')"
    />
    <validation-observer ref="deviceForm">
      <b-row>
        <b-col md="4">
          <validation-provider
            #default="{ errors }"
            name="Device ID"
            rules="required"
          >
            <b-form-group
              label="Device ID"
              label-for="device-id"
            >
              <b-form-input
                id="device-id"
                v-model="formData.device_id"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="4">
          <b-form-group
            label="Device Type"
            label-for="device-type"
          >
            <b-form-select
              id="device-type"
              v-model="formData.device_type"
              :options="deviceTypes"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <validation-provider
            #default="{ errors }"
            name="Application Version"
            rules="required"
          >
            <b-form-group
              label="Application Version"
              label-for="app-version"
            >
              <b-form-input
                id="app-version"
                v-model="formData.application_version"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="4">
          <validation-provider
            #default="{ errors }"
            name="Operating System"
            rules="required"
          >
            <b-form-group
              label="Operating System"
              label-for="operating-system"
            >
              <b-form-input
                id="operating-system"
                v-model="formData.operating_system"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="4">
          <b-form-group
            label="Status"
            label-for="status"
          >
            <b-form-select
              id="status"
              v-model="formData.status"
              :options="statuses"
              :disabled="!data"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            label="Last Login At (Date)"
            label-for="last-login-date"
          >
            <b-form-datepicker
              v-model="dateAt"
              disabled
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            label="Last Login At (Time)"
            label-for="last-login-time"
          >
            <b-form-timepicker
              v-model="timeAt"
              disabled
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            label="Added By"
            label-for="added-by"
          >
            <b-form-select
              id="added-by"
              v-model="formData.added_by"
              :options="addedByItems"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="4"
          class="d-flex align-items-center"
        >
          <b-form-group
            label="Supports Google"
            label-for="sup-google"
          >
            <b-form-checkbox
              id="sup-google"
              v-model="formData.supports_google"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="4"
          class="d-flex align-items-center"
        >
          <b-button
            v-if="$can('write', 'driver-devices')"
            variant="primary"
            type="submit"
            :disabled="submitting"
          >
            <BSpinner
              v-if="submitting"
              small
            />
            <span v-else>Submit</span>
          </b-button>
        </b-col>
        <b-col
          md="4"
          class="d-flex align-items-center"
        >
          <b-button
            v-if="$can('write', 'driver-devices') && formData.id"
            variant="primary"
            :disabled="formData.status === 'REJECTED'"
            @click="blockDeviceHandler"
          >
            <span>Block</span>
          </b-button>
        </b-col>
      </b-row>
    </validation-observer>
    <hr>
  </b-form>
</template>

<script>
import {
  BForm, BRow, BCol, BFormInput, BFormSelect, BFormGroup, BFormDatepicker, BFormTimepicker, BFormCheckbox, BButton, BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { reactive, ref } from '@vue/composition-api'
import BlockDevice from './BlockDevice.vue'

export default {
  components: {
    BlockDevice,
    BForm,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    BFormInput,
    BFormSelect,
    BFormGroup,
    BFormDatepicker,
    BFormTimepicker,
    BFormCheckbox,
    BButton,
    BSpinner,
  },
  props: {
    data: {
      type: Object,
      default: undefined,
    },
    driver: {
      type: Object,
      required: true,
    },
  },
  setup(props, ctx) {
    const deviceTypes = [
      {
        text: 'Android',
        value: 'ANDROID',
      },
      {
        text: 'Ios',
        value: 'IOS',
      },
      {
        text: 'Huawei',
        value: 'HUAWEI',
      },
    ]
    const statuses = [
      {
        text: 'Pending',
        value: 'PENDING',
      },
      {
        text: 'Rejected',
        value: 'REJECTED',
      },
      {
        text: 'Approved',
        value: 'APPROVED',
      },
      {
        text: 'Deleted',
        value: 'DELETED',
      },

    ]
    const addedByItems = [
      {
        text: 'Manual',
        value: 'MANUAL',
      },
      {
        text: 'System',
        value: 'SYSTEM',
      },
    ]
    const dateAt = ref(null)
    const timeAt = ref(null)
    const deviceForm = ref(null)
    const submitting = ref(false)
    const formData = reactive({
      device_id: null,
      device_type: 'ANDROID',
      supports_google: false,
      application_version: null,
      operating_system: null,
      status: 'PENDING',
      added_by: 'MANUAL',
    })
    if (props.data && !props.data.newDevice) {
      Object.keys(props.data).forEach(k => {
        formData[k] = props.data[k]
      })
      const lastLoginAt = props.data.last_login_at
      if (lastLoginAt) {
        const temp = lastLoginAt.split('T')[0]
        dateAt.value = temp
        timeAt.value = new Date(lastLoginAt).toLocaleTimeString('en', { timeStyle: 'short', hour12: false })
      }
    }
    const submit = async () => {
      const valid = await deviceForm.value.validate()
      if (!valid) return
      submitting.value = true
      try {
        const body = {
          id: props.driver.id,
          deviceId: formData.device_id,
          data: formData,
        }
        if (!props.data.newDevice) {
          await ctx.root.$store.dispatch('driverView/patchDriverDeviceInfo', body)
        } else {
          await ctx.root.$store.dispatch('driverView/createDriverDeviceInfo', body)
        }
        ctx.emit('submitted')
      } catch (error) {
        console.log(error.response)
      } finally {
        submitting.value = false
      }
    }

    const toBeDeletedDeviceId = ref(null)
    const blockDeviceHandler = () => {
      toBeDeletedDeviceId.value = formData.id
      ctx.root.$bvModal.show(`confirm-block-modal-${formData.id}`)
    }

    return {
      blockDeviceHandler,
      required,
      deviceTypes,
      addedByItems,
      statuses,
      formData,
      toBeDeletedDeviceId,
      deviceForm,
      dateAt,
      timeAt,
      submit,
      submitting,
    }
  },
}
</script>

<style>

</style>
